<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide">
    <q-card :style="{ minWidth: '600px' }" class="relative-position">
      <PDialogHeader
        :title="formHeading"
        :closable="true"
        @close="onDialogHide"
      />
      <q-form greedy @submit.prevent="handleSubmit">
        <q-card-section class="q-px-2x-md q-pt-xl q-pb-none">
          <PSelectInput
            v-model.trim="formData.receiversList"
            :options="unref(receiversFilter.entities)"
            :options-loading="unref(receiversFilter.loading)"
            :validations="{
              required: true,
            }"
            label="To"
            option-label="email"
            virtual-scroll-slice-size="25"
            virtual-scroll-item-size="53"
            multiple
            use-chips
            @filter="receiversFilter.onFilter"
            @filter-abort="receiversFilter.onFilterAbort"
            @virtual-scroll="receiversFilter.onScroll"
          >
            <template #option="{ itemProps, opt }">
              <q-item v-bind="itemProps" :disable="!opt.is_active">
                <q-item-section>
                  <q-item-label class="text-sm">
                    <span class="text-neutral">{{
                      opt.email || opt.name
                    }}</span>
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </PSelectInput>

          <PSelectInput
            v-model.trim="formData.ccList"
            :options="unref(ccFilter.entities)"
            :options-loading="unref(ccFilter.loading)"
            label="CC"
            option-label="email"
            virtual-scroll-slice-size="25"
            virtual-scroll-item-size="53"
            multiple
            use-chips
            @filter="ccFilter.onFilter"
            @filter-abort="ccFilter.onFilterAbort"
            @virtual-scroll="ccFilter.onScroll"
          >
            <template #option="{ itemProps, opt }">
              <q-item v-bind="itemProps" :disable="!opt.is_active">
                <q-item-section>
                  <q-item-label class="text-sm">
                    <span class="text-neutral">{{
                      opt.email || opt.name
                    }}</span>
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </PSelectInput>
          <PField
            v-model="formData.htmlContent"
            class="q-py-sm"
            :validations="{
              required: true,
              type: 'html',
            }"
          >
            <template #control>
              <PEditor v-model="formData.htmlContent" />
            </template>
          </PField>
        </q-card-section>
        <q-card-section>
          <div class="flex justify-end q-pt-sm">
            <PBtn v-bind="primaryCtaProps" type="submit" />
          </div>
        </q-card-section>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { useDialogPluginComponent, debounce } from "quasar";

const { dialogRef, onDialogHide, onDialogOK } = useDialogPluginComponent();

import { postCreditLimitIncreaseRequest } from "src/api/invoice";
import { useCreditLimitRequest } from "../composables/creditLimitRequest";
import PDialogHeader from "src/components/PDialogHeader.vue";

const isSubmitting = ref(false);

const props = defineProps({
  details: {
    type: Object,
    required: true,
  },
  disableCreditLimitIncreaseRequest: {
    type: Function,
    required: true,
  },
});

const {
  receiversList,
  ccList,
  formHeading,

  getInitialState,
  getReceiversAndCCList,
} = useCreditLimitRequest({
  props,
});

const formData = ref(getInitialState());

const receiverIds = computed(() =>
  unref(formData).receiversList.map((receiver) => receiver._id),
);

const ccIds = computed(() => unref(formData).ccList.map((cc) => cc._id));

const receiversFilter = computed(() =>
  useFilter({
    entity: "user",
    options: {
      params: {
        is_internal_user: true,
        isActive: 1,
        skip: unref(ccIds).join(","),
      },
    },
  }),
);

const ccFilter = computed(() =>
  useFilter({
    entity: "user",
    options: {
      params: {
        is_internal_user: true,
        isActive: 1,
        skip: unref(receiverIds).join(","),
      },
    },
  }),
);

const populateReceiversAndCCList = debounce(async () => {
  let list = await getReceiversAndCCList([...receiversList, ...ccList]);

  formData.value.receiversList = list.filter(
    (user) =>
      receiversList.includes(user.email) ||
      user.type === "account_owner" ||
      user.type == "invoice_requestor",
  );

  const receiverListEmails = new Set(
    unref(formData).receiversList.map((user) => user.email),
  );

  formData.value.ccList = list.filter(
    (user) =>
      ccList.includes(user.email) && !receiverListEmails.has(user.email),
  );
}, 100);

nextTick(() => {
  populateReceiversAndCCList();
});

const handleSubmit = async () => {
  try {
    isSubmitting.value = true;

    await postCreditLimitIncreaseRequest(unref(formData));

    props?.disableCreditLimitIncreaseRequest(true);
  } catch (error) {
    console.error(error);

    props?.disableCreditLimitIncreaseRequest(false);
  } finally {
    isSubmitting.value = false;

    onDialogOK();
  }
};

const primaryCtaProps = computed(() => ({
  label: "Send",
  fluid: false,
  loading: unref(isSubmitting),
}));
</script>
